import React, { useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis
} from 'recharts';
import {
  ShieldCheckIcon,
  TrophyIcon,
  ArrowTrendingUpIcon,
  LightBulbIcon,
  UserGroupIcon,
  GlobeAltIcon,
  ExclamationTriangleIcon,
  CheckBadgeIcon
} from '@heroicons/react/24/outline';

const COLORS = ['#f97316', '#3b82f6', '#10b981', '#8b5cf6', '#ec4899', '#06b6d4'];

function StatusBadge({ status }) {
  const styles = {
    Strong: 'bg-green-900/50 text-green-400 border-green-700',
    Moderate: 'bg-yellow-900/50 text-yellow-400 border-yellow-700',
    'Needs Work': 'bg-red-900/50 text-red-400 border-red-700'
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${styles[status] || styles['Needs Work']}`}>
      {status}
    </span>
  );
}

function PriorityBadge({ priority }) {
  const styles = {
    High: 'bg-red-900/40 text-red-400',
    Medium: 'bg-yellow-900/40 text-yellow-400',
    Low: 'bg-blue-900/40 text-blue-400'
  };
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${styles[priority] || ''}`}>{priority}</span>;
}

function ThreatBadge({ threat }) {
  const styles = {
    High: 'text-red-400',
    Medium: 'text-yellow-400',
    Low: 'text-green-400'
  };
  return <span className={`text-xs font-semibold ${styles[threat] || ''}`}>{threat} Threat</span>;
}

const BrandImpactAnalysis = ({ data }) => {
  const [activeTab, setActiveTab] = useState('overview');

  if (!data) return null;

  const { brandStrength, marketPosition, competitorComparison, brandMentions, shareOfVoice, brandAuthority, impactDrivers, opportunities } = data;

  // Brand Strength radar data
  const strengthRadar = [
    { metric: 'Awareness', value: brandStrength?.awareness || 0 },
    { metric: 'Authority', value: brandStrength?.authority || 0 },
    { metric: 'Reputation', value: brandStrength?.reputation || 0 },
    { metric: 'Consistency', value: brandStrength?.consistency || 0 }
  ];

  // Share of Voice pie data
  const sovData = [
    { name: 'Organic', value: shareOfVoice?.organic || 0 },
    { name: 'Branded', value: shareOfVoice?.branded || 0 },
    { name: 'Non-Branded', value: shareOfVoice?.nonBranded || 0 }
  ];

  // Impact drivers bar data
  const driverBarData = (impactDrivers || []).map(d => ({
    name: d.driver,
    impact: d.impact,
    fill: d.status === 'Strong' ? '#10b981' : d.status === 'Moderate' ? '#f59e0b' : '#ef4444'
  }));

  const tabs = [
    { id: 'overview', label: 'Overview', icon: ShieldCheckIcon },
    { id: 'competitors', label: 'Competitors', icon: UserGroupIcon },
    { id: 'opportunities', label: 'Opportunities', icon: LightBulbIcon },
  ];

  const positionColor = {
    Leader: 'text-green-400',
    Challenger: 'text-blue-400',
    Follower: 'text-yellow-400',
    'Niche Player': 'text-purple-400',
    Emerging: 'text-orange-400'
  };

  return (
    <div className="space-y-8">
      {/* Brand Strength Header */}
      <div className="bg-white border border-gray-200 rounded-2xl p-8">
        <div className="flex flex-col md:flex-row items-center gap-8">
          <div className="text-center">
            <div className="relative w-32 h-32 mx-auto">
              <svg className="w-32 h-32 transform -rotate-90" viewBox="0 0 120 120">
                <circle cx="60" cy="60" r="52" fill="none" stroke="#374151" strokeWidth="8" />
                <circle cx="60" cy="60" r="52" fill="none"
                  stroke={brandStrength?.overall >= 70 ? '#10b981' : brandStrength?.overall >= 40 ? '#f59e0b' : '#ef4444'}
                  strokeWidth="8" strokeLinecap="round"
                  strokeDasharray={`${(brandStrength?.overall / 100) * 327} 327`}
                  className="transition-all duration-1000" />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-3xl font-bold text-gray-900">{brandStrength?.overall || 0}</span>
                <span className="text-xs text-gray-600">/100</span>
              </div>
            </div>
            <p className="text-sm text-gray-600 mt-2">Brand Strength</p>
          </div>

          <div className="flex-1 text-center md:text-left">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Brand Impact Analysis</h2>
            <p className="text-gray-600 mb-1">Domain: <span className="text-orange-400 font-medium">{data.domain}</span></p>
            <div className="mt-3 flex flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <TrophyIcon className="w-5 h-5 text-orange-400" />
                <span className="text-gray-700">Market Position: </span>
                <span className={`font-semibold ${positionColor[marketPosition?.ranking] || 'text-gray-600'}`}>
                  {marketPosition?.ranking || 'Unknown'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <GlobeAltIcon className="w-5 h-5 text-blue-400" />
                <span className="text-gray-700">SERP Ownership: </span>
                <span className="font-semibold text-gray-900">{brandMentions?.serpOwnership || 0}%</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckBadgeIcon className="w-5 h-5 text-green-400" />
                <span className="text-gray-700">Authority: </span>
                <span className="font-semibold text-gray-900">{brandAuthority?.score || 0}/100</span>
              </div>
            </div>
            {marketPosition?.description && (
              <p className="text-gray-500 text-sm mt-3 italic">{marketPosition.description}</p>
            )}
          </div>

          {/* Mini strength metrics */}
          <div className="grid grid-cols-2 gap-3">
            {strengthRadar.map((item) => (
              <div key={item.metric} className="bg-gray-50 rounded-lg p-3 text-center min-w-[100px]">
                <div className="text-lg font-bold text-orange-400">{item.value}</div>
                <div className="text-xs text-gray-600">{item.metric}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-gray-200">
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id ? 'border-orange-500 text-orange-400' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}>
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Brand Strength Radar */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Brand Strength Breakdown</h3>
            <ResponsiveContainer width="100%" height={280}>
              <RadarChart data={strengthRadar}>
                <PolarGrid stroke="#374151" />
                <PolarAngleAxis dataKey="metric" tick={{ fill: '#d1d5db', fontSize: 15 }} />
                <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fill: '#d1d5db', fontSize: 13 }} />
                <Radar dataKey="value" stroke="#f97316" fill="#f97316" fillOpacity={0.3} strokeWidth={2} />
              </RadarChart>
            </ResponsiveContainer>
          </div>

          {/* Share of Voice */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Share of Voice</h3>
            <div className="flex items-center gap-6">
              <ResponsiveContainer width="50%" height={200}>
                <PieChart>
                  <Pie data={sovData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={5} dataKey="value">
                    {sovData.map((entry, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px', color: '#fff' }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-3">
                <div className="text-center">
                  <div className="text-3xl font-bold text-orange-400">{shareOfVoice?.overall || 0}%</div>
                  <div className="text-xs text-gray-600">Overall SOV</div>
                </div>
                {sovData.map((item, i) => (
                  <div key={item.name} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i] }} />
                    <span className="text-sm text-gray-600">{item.name}: <span className="text-gray-900 font-medium">{item.value}%</span></span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Impact Drivers */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Impact Drivers</h3>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={driverBarData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis type="number" domain={[0, 100]} tick={{ fill: '#d1d5db' }} />
                <YAxis type="category" dataKey="name" tick={{ fill: '#d1d5db', fontSize: 14 }} width={120} />
                <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px', color: '#fff' }} />
                <Bar dataKey="impact" radius={[0, 4, 4, 0]}>
                  {driverBarData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Brand Authority Card */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Brand Authority</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600">Authority Score</span>
                <span className="text-xl font-bold text-orange-400">{brandAuthority?.score || 0}/100</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600">Trust Signals</span>
                <span className="text-gray-900 font-medium">{brandAuthority?.trustSignals || 0}/10</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600">Backlink Quality</span>
                <StatusBadge status={brandAuthority?.backlinkQuality === 'Strong' ? 'Strong' : brandAuthority?.backlinkQuality === 'Moderate' ? 'Moderate' : 'Needs Work'} />
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600">Domain Strength</span>
                <span className={`font-medium ${brandAuthority?.domainStrength === 'High' ? 'text-green-400' : brandAuthority?.domainStrength === 'Medium' ? 'text-yellow-400' : 'text-red-400'}`}>
                  {brandAuthority?.domainStrength || 'Low'}
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600">Brand Sentiment</span>
                <span className={`font-medium ${brandMentions?.sentiment === 'Positive' ? 'text-green-400' : brandMentions?.sentiment === 'Negative' ? 'text-red-400' : 'text-yellow-400'}`}>
                  {brandMentions?.sentiment || 'Neutral'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Competitors Tab */}
      {activeTab === 'competitors' && (
        <div className="space-y-4">
          {(competitorComparison || []).length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
              <UserGroupIcon className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-600">No competitor data available for this domain.</p>
            </div>
          ) : (
            (competitorComparison || []).map((comp, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="text-gray-900 font-semibold text-lg">{comp.domain}</h4>
                    <ThreatBadge threat={comp.threat} />
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-orange-400">{comp.overlapScore}%</div>
                    <div className="text-xs text-gray-500">Keyword Overlap</div>
                  </div>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2 mb-4">
                  <div className="h-2 rounded-full bg-orange-500 transition-all duration-1000" style={{ width: `${comp.overlapScore}%` }} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-red-900/20 rounded-lg border border-red-900/30">
                    <div className="text-xs text-red-400 font-medium mb-1 flex items-center gap-1">
                      <ExclamationTriangleIcon className="w-3 h-3" /> Their Advantage
                    </div>
                    <p className="text-gray-700 text-sm">{comp.advantage}</p>
                  </div>
                  <div className="p-3 bg-green-900/20 rounded-lg border border-green-900/30">
                    <div className="text-xs text-green-400 font-medium mb-1 flex items-center gap-1">
                      <CheckBadgeIcon className="w-3 h-3" /> Your Edge
                    </div>
                    <p className="text-gray-700 text-sm">{comp.yourEdge}</p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Opportunities Tab */}
      {activeTab === 'opportunities' && (
        <div className="space-y-4">
          {(opportunities || []).map((opp, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-xl p-6">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-500/20 rounded-lg">
                    <LightBulbIcon className="w-5 h-5 text-orange-400" />
                  </div>
                  <div>
                    <h4 className="text-gray-900 font-semibold">{opp.title}</h4>
                    <p className="text-gray-600 text-sm mt-1">{opp.description}</p>
                  </div>
                </div>
                <PriorityBadge priority={opp.priority} />
              </div>
              <div className="mt-3 flex items-center gap-4 text-xs text-gray-500">
                <span>Effort: <span className={`font-medium ${opp.effort === 'Low' ? 'text-green-400' : opp.effort === 'Medium' ? 'text-yellow-400' : 'text-red-400'}`}>{opp.effort}</span></span>
                <span>Priority: <span className={`font-medium ${opp.priority === 'High' ? 'text-red-400' : opp.priority === 'Medium' ? 'text-yellow-400' : 'text-blue-400'}`}>{opp.priority}</span></span>
              </div>
            </div>
          ))}

          {/* Market Position Summary */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 mt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Market Position Summary</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h4 className="text-green-400 text-sm font-medium mb-2">Strength Areas</h4>
                {(marketPosition?.strengthAreas || []).map((s, i) => (
                  <div key={i} className="flex items-center gap-2 mb-2">
                    <div className="w-1.5 h-1.5 bg-green-400 rounded-full" />
                    <span className="text-gray-700 text-sm">{s}</span>
                  </div>
                ))}
              </div>
              <div>
                <h4 className="text-red-400 text-sm font-medium mb-2">Weakness Areas</h4>
                {(marketPosition?.weaknessAreas || []).map((w, i) => (
                  <div key={i} className="flex items-center gap-2 mb-2">
                    <div className="w-1.5 h-1.5 bg-red-400 rounded-full" />
                    <span className="text-gray-700 text-sm">{w}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BrandImpactAnalysis;
